/**
 * DashboardPreferencesNudge.js — RC 1.43.C.1
 *
 * Honest, low-friction nudge encouraging candidates to set their
 * preferences. Appears on the dashboard when the candidate has zero
 * active preferences — auto-suppresses the moment any preference is
 * saved, and respects an explicit "Maybe later" dismissal stored in
 * localStorage so it never nags.
 *
 * Reads the same `/api/profile/preferences` endpoint as
 * PreferencesCard. Auto-refreshes when the editor saves
 * (`ndor:preferences-changed` event).
 *
 * No backend changes. Pure client-side card.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, X as XIcon } from 'lucide-react';

import { apiFetch } from '@/lib/api';
import { countActivePreferences } from '@/components/preferencesHelpers';

const DISMISS_KEY = 'ndor:dashboard-preferences-nudge:dismissed';

function isDismissed() {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function markDismissed() {
  try { window.localStorage.setItem(DISMISS_KEY, '1'); } catch { /* */ }
}

export default function DashboardPreferencesNudge() {
  const [prefs, setPrefs]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [dismissed, setDismissed] = useState(() => isDismissed());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/profile/preferences');
      setPrefs(r?.preferences || {});
    } catch {
      setPrefs(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh when the editor saves so a populated state hides the
  // card immediately without waiting for a remount.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener('ndor:preferences-changed', onChanged);
    return () => window.removeEventListener('ndor:preferences-changed', onChanged);
  }, [load]);

  const count = useMemo(() => countActivePreferences(prefs), [prefs]);

  // Auto-suppress conditions:
  //  1. Still loading → render nothing (avoid flash).
  //  2. User already dismissed.
  //  3. User has at least one active preference.
  if (loading) return null;
  if (dismissed) return null;
  if (count > 0) return null;

  const onMaybeLater = () => {
    markDismissed();
    setDismissed(true);
  };

  return (
    <Card
      className="border-ndor-edge bg-ndor-surface relative"
      data-testid="dashboard-preferences-nudge"
    >
      <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="shrink-0 w-10 h-10 rounded-full bg-ndor-gold/10 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-ndor-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-ndor-ink">
            Make NDOR Analyse Fit personal.
          </h3>
          <p className="text-xs text-ndor-ink-muted mt-1 leading-relaxed">
            Set salary, industry, seniority, and exclusions in{' '}
            <span className="text-ndor-ink">Account → Preferences</span>{' '}
            so every fit score reflects what you actually want.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost" size="sm"
            onClick={onMaybeLater}
            data-testid="dashboard-preferences-nudge-dismiss"
          >
            Maybe later
          </Button>
          <Link to="/account?tab=preferences">
            <Button
              size="sm"
              data-testid="dashboard-preferences-nudge-cta"
              className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
            >
              Set preferences
            </Button>
          </Link>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onMaybeLater}
          className="absolute top-2 right-2 text-ndor-ink-muted hover:text-ndor-ink p-1"
          data-testid="dashboard-preferences-nudge-close"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </CardContent>
    </Card>
  );
}
